
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import axios from '../../services/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import LoadingIndicator from '../LoadingIndicator';
import './SamlLoginPage.css';

/**
 * Auth Redirect Page Component
 * Automatically checks authentication status and redirects accordingly
 */
const SamlLoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { refreshAuth } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [urlParams, setUrlParams] = useState({});
    const authCheckRef = useRef(false);
    
    // Use environment variable for API URL (consistent with CustomReports pattern)
    const API_URL = process.env.REACT_APP_API_URL;

    // Utility function to get cookie value by name
    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    };

    // Check if authentication token exists in cookies or sessionStorage
    const isTokenAvailable = () => {
        const cookieToken = getCookie('auth_token') || getCookie('authToken') || getCookie('token') || getCookie('jwt');
        const sessionToken = sessionStorage.getItem('authToken') || sessionStorage.getItem('token');
        
        return !!(cookieToken || sessionToken);
    };

    // Extract URL parameters and check auth status immediately
    useEffect(() => {
        // Prevent multiple auth checks
        if (authCheckRef.current) {
            return;
        }
        
        authCheckRef.current = true;
        
        const checkAuthAndProceed = async () => {
            try {
                console.log('Checking authentication status...');
                // Dynamically extract all query parameters without altering case
                const queryParams = new URLSearchParams(location.search);
                const params = {};
                queryParams.forEach((value, key) => {
                    params[key] = value; // Keep keys as they are
                });
                // Map parameters to expected format with specific camelCase format
                const extractedParams = {
                    userID: params.userId || params.userID || params.UserID || '',
                    memberId: params.memberId || params.MemberID || '',
                    cid: params.cid || params.CID || '',
                    caseId: params.caseId || params.CaseID || '',
                    systemId: params.systemId || params.SystemId || params.SystemID || ''
                };

                // Require only userID; other params may be null/undefined
                const isQueryEmpty = Object.keys(params).length === 0;
                if (isQueryEmpty || !extractedParams.userID) {
                    navigate('/error?reason=missing-userid', { replace: true });
                    return;
                }

                setUrlParams(extractedParams);
                // Member context → sessionStorage ONLY (tab-scoped; fresh from URL each new tab).
                // Do NOT write member params to localStorage — they must come from the URL
                // on every new tab open so stale values can never leak across sessions.
                sessionStorage.setItem('memberContext', JSON.stringify({
                    memberId: extractedParams.memberId || '',
                    cid:      extractedParams.cid      || '',
                    caseId:   extractedParams.caseId   || '',
                    systemId: extractedParams.systemId || ''
                }));
                // samlRequestParams kept session-only for the SAML relay-state flow
                sessionStorage.setItem('samlRequestParams', JSON.stringify(extractedParams));

                // Check authentication status via API
                const authStatusResponse = await axios.get(`${API_URL}/Login/check-auth-status`);
                console.log('Auth status response:', authStatusResponse.data);

                // Check if user is authenticated (handle both IsAuthenticated and isAuthenticated)
                const isAuthenticated = authStatusResponse.data?.IsAuthenticated || authStatusResponse.data?.isAuthenticated;

                if (isAuthenticated) {
                    console.log('User is already authenticated, preparing to call Main API');
                    sessionStorage.setItem('isAuthenticated', 'true');
                    const resolvedUserId = authStatusResponse.data?.UserId || authStatusResponse.data?.userId || '';

                    // User identity → localStorage (survives tab close; enables skip-re-auth on re-entry)
                    const userIdentity = {
                        userId:   resolvedUserId,
                        userName: authStatusResponse.data?.Username || authStatusResponse.data?.username || '',
                        email:    authStatusResponse.data?.Email    || authStatusResponse.data?.email    || '',
                        roles:    authStatusResponse.data?.Roles    || authStatusResponse.data?.roles    || [],
                        isAuthenticated: true
                    };
                    localStorage.setItem('userIdentity', JSON.stringify(userIdentity));

                    // sessionStorage.user — backward-compat composite (identity + this tab's member context)
                    const userInfo = {
                        ...userIdentity,
                        memberId: extractedParams.memberId || '',
                        cid:      extractedParams.cid      || '',
                        caseId:   extractedParams.caseId   || '',
                        systemId: extractedParams.systemId || ''
                    };
                    sessionStorage.setItem('user', JSON.stringify(userInfo));
                    // Refresh auth context
                    await refreshAuth();
                    // Import and call the Main API with the extracted parameters
                    try {
                        const { callMainAPI } = await import('../../services/authService');
                        console.log('Calling Main API with parameters:', extractedParams);
                        const apiResponse = await callMainAPI(extractedParams);
                        if (!apiResponse) {
                            console.warn('Main API call returned no data');
                        } else {
                            console.log('Main API call successful');
                        }
                        // Redirect to reports-list page with preserved parameters
                        navigate('/reports-list' + location.search, { replace: true });
                    } catch (apiError) {
                        console.error('Error calling Main API:', apiError);
                        // Continue to reports list even if Main API call fails
                        navigate('/reports-list' + location.search, { replace: true });
                    }
                } else {
                    console.log('User is not authenticated, proceeding with SAML login');
                    // Not authenticated, initiate SAML login flow
                    await initiateLogin(extractedParams, params);
                }
            } catch (error) {
                console.error('Auth check error:', error);
                setError('Authentication check failed. Please try again.');
                // If auth check fails, try SAML login as fallback
                const queryParams = new URLSearchParams(location.search);
                const params = {};
                queryParams.forEach((value, key) => {
                    params[key] = value;
                });
                const extractedParams = {
                    userID: params.userId || params.userID || params.UserID || '',
                    memberId: params.memberId || params.MemberID || '',
                    cid: params.cid || params.CID || '',
                    caseId: params.caseId || params.CaseID || '',
                    systemId: params.systemId || params.SystemId || params.SystemID || ''
                };
                // Require only userID for fallback path as well
                const isQueryEmpty = Object.keys(params).length === 0;
                if (isQueryEmpty || !extractedParams.userID) {
                    navigate('/error?reason=missing-userid', { replace: true });
                    return;
                }
                // Store params session-only for the SAML relay-state flow;
                // member params must NOT be persisted to localStorage.
                sessionStorage.setItem('samlRequestParams', JSON.stringify(extractedParams));
                sessionStorage.setItem('memberContext', JSON.stringify({
                    memberId: extractedParams.memberId || '',
                    cid:      extractedParams.cid      || '',
                    caseId:   extractedParams.caseId   || '',
                    systemId: extractedParams.systemId || ''
                }));
                await initiateLogin(extractedParams, params);
            } finally {
                setLoading(false);
            }
        };
        
        checkAuthAndProceed();
    }, [location.search, API_URL, navigate, refreshAuth]);

    /**
     * Initiate SAML login process
     */
    const initiateLogin = async (extractedParams, params) => {
        try {
            // Require only userID; other params may be null when initiating SAML login
            const isQueryEmpty = !params || Object.keys(params).length === 0;
            if (isQueryEmpty || !extractedParams.userID) {
                navigate('/error?reason=missing-userid', { replace: true });
                return;
            }
            console.log('Initiating SAML login process with params:', extractedParams);
            // Make the SAML request
            const response = await axios.post(`${API_URL}/Login/saml-request`, extractedParams);
            if (response.data && response.data.redirectUrl) {
                console.log('Received SAML redirect URL:', response.data.redirectUrl);
                // Redirect to SAML IdP in the same tab
                window.location.href = response.data.redirectUrl;
            } else {
                throw new Error('Invalid SAML response from server');
            }
        } catch (err) {
            console.error('SAML login error:', err);
            // Don't update UI with error messages
            // Just log to console and keep spinner showing
        }
    };

    return (
        <Container fluid className="auth-check-container">
            <Row className="justify-content-center align-items-center min-vh-100">
                <Col xs={12} className="text-center">
                    <LoadingIndicator isLoading={true} />
                </Col>
            </Row>
        </Container>
    );
};

export default SamlLoginPage;
